import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const catDropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    function handleScroll() {
      setScrolled(window.scrollY > 60);
    }
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (catDropdownRef.current && !catDropdownRef.current.contains(event.target as Node)) {
        setCategoriesOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
    setCategoriesOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === '/';

  const navLinks = [
    { label: 'Início', href: '/', internal: true },
    { label: 'Guia de Compras', href: '/guia-de-compras', internal: true },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-300 ${
        scrolled || !isHome
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-[4.5rem]">
          {/* Logo / Brand */}
          <a
            href="/"
            className={`font-heading font-bold text-xl md:text-2xl whitespace-nowrap transition-colors duration-300 ${
              scrolled || !isHome ? 'text-foreground-950' : 'text-white'
            }`}
          >
            CSSBuy<span className="font-normal">Info</span>
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            {/* Home */}
            <a
              href="/"
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors duration-200 ${
                scrolled || !isHome
                  ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                  : 'text-white/80 hover:text-white hover:bg-white/10'
              } ${location.pathname === '/' ? (scrolled || !isHome ? 'bg-background-100 text-foreground-950' : 'bg-white/15 text-white') : ''}`}
            >
              Início
            </a>

            {/* Shopping Guide */}
            <a
              href="/guia-de-compras"
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors duration-200 ${
                scrolled || !isHome
                  ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                  : 'text-white/80 hover:text-white hover:bg-white/10'
              } ${location.pathname === '/guia-de-compras' ? (scrolled || !isHome ? 'bg-background-100 text-foreground-950' : 'bg-white/15 text-white') : ''}`}
            >
              Guia de Compras
            </a>

            {/* Categories Dropdown */}
            <div className="relative" ref={catDropdownRef}>
              <button
                onClick={() => setCategoriesOpen(!categoriesOpen)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors duration-200 cursor-pointer ${
                  scrolled || !isHome
                    ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                } ${categoriesOpen ? (scrolled || !isHome ? 'bg-background-100 text-foreground-950' : 'bg-white/15 text-white') : ''}`}
              >
                Categorias
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${categoriesOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {categoriesOpen && (
                <div className={`absolute top-full right-0 mt-2 w-56 rounded-2xl border shadow-sm overflow-hidden ${
                  scrolled || !isHome ? 'bg-background-50 border-background-200' : 'bg-white border-white/20'
                }`}>
                  <div className="max-h-[400px] overflow-y-auto py-2">
                    {categories.map((cat) => (
                      <a
                        key={cat.id}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer"
                        onClick={() => setCategoriesOpen(false)}
                      >
                        <span className="w-5 h-5 flex items-center justify-center">
                          <i className={`${cat.icon} text-foreground-500 text-sm`}></i>
                        </span>
                        <span className="whitespace-nowrap">{cat.name}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* CTA */}
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="ml-3 inline-flex items-center gap-1.5 bg-accent-500 text-white font-semibold px-5 py-2 rounded-full text-sm whitespace-nowrap hover:bg-accent-600 transition-colors duration-200 cursor-pointer"
            >
              CSSBuy Sheet
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-full transition-colors cursor-pointer ${
              scrolled || !isHome ? 'text-foreground-950' : 'text-white'
            }`}
            aria-label="Menu"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/60">
          <div className="px-4 py-4 space-y-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className={`block px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                  location.pathname === link.href
                    ? 'bg-background-100 text-foreground-950'
                    : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-950'
                }`}
              >
                {link.label}
              </a>
            ))}

            {/* Mobile Categories */}
            <div className="pt-2">
              <p className="px-4 py-2 text-xs text-foreground-500 font-semibold uppercase">Categorias</p>
              <div className="grid grid-cols-2 gap-1">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className={`${cat.icon} text-foreground-400 text-xs`}></i>
                    </span>
                    <span className="whitespace-nowrap truncate">{cat.name}</span>
                  </a>
                ))}
              </div>
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="block mt-3 mx-4 px-4 py-3 bg-accent-500 text-white font-semibold rounded-xl text-sm text-center hover:bg-accent-600 transition-colors"
            >
              CSSBuy Sheet
              <i className="ri-arrow-right-up-line ml-1.5 text-xs"></i>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}